/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { MessageService } from '@src/app/shared/message.service';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { AbstractObject, TenantId, uuid } from '@src/app/model/abstract-object';
import { PurchaseDocument, decoratePurchaseDocuments } from '@src/app/model/purchaseDocument';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { Txn } from '@src/app/model/txn';
import { AllocateCreditNoteAction } from '@src/app/modules/txn/purchase-page/AllocateCreditNoteAction';
import { PurchasePageNavRoute } from '@src/app/routes/purchase-page-nav';
import { BankOutPageNavRoute } from '@src/app/routes/bank-out-page-nav';

@Injectable({
    providedIn: 'root'
})
export class PurchaseService extends AbstractHttpService {
    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);


    protected get baseUrl(): string {
        return this.getAjaxPath(Txn.TYPE.PURCHASE.code);
    }
    protected cache: Txn[];
    protected typeString = Txn.TYPE.PURCHASE.name;

    approve(object: Txn): Observable<Txn> {
        return this.doApproval(object, true);
    }

    unapprove(object: Txn): Observable<Txn> {
        return this.doApproval(object, false);
    }

    getNewOptions() {
        return [{
            name: Txn.TYPE.PURCHASE.name,
            basePath: PurchasePageNavRoute.newUrl,
            params: { typeId: Txn.TYPE.PURCHASE.id },
            id: 'newPurchase'
        },{
            name: Txn.TYPE.BANK_OUT.name,
            basePath: BankOutPageNavRoute.newUrl,
            id: 'newPayment'
        },{
            name: Txn.TYPE.CASH_PURCHASE.name,
            basePath: PurchasePageNavRoute.newUrl,
            params: { typeId: Txn.TYPE.CASH_PURCHASE.id},
            id: 'newCashPurchase'
        },{
            name: Txn.TYPE.PURCHASE_CN.name,
            basePath: PurchasePageNavRoute.newUrl,
            params: { typeId: Txn.TYPE.PURCHASE_CN.id },
            id: 'newPurchaseCreditNote'
        },{
            name: Txn.TYPE.PURCHASE_ACCRUAL.name,
            basePath: PurchasePageNavRoute.newUrl,
            params: { typeId: Txn.TYPE.PURCHASE_ACCRUAL.id},
            id: 'newPurchaseAccrual'
        },{
            name: Txn.TYPE.PURCHASE_CN_ACCRUAL.name,
            basePath: PurchasePageNavRoute.newUrl,
            params: { typeId: Txn.TYPE.PURCHASE_CN_ACCRUAL.id},
            id: 'newPurchaseCreditNoteAccrual'
        }];
    }

    /**
     * Overriding the post method, to add extra functionality after form saves
     * Adding multiple method signatures, lets typescript know, if method called with a Txn it return a Txn
     * Without requiring <Txn> when calling, and without requiring special casting of the return value
     */
    post<T extends AbstractObject>(object: T, showMsg?: boolean, teamId?: TenantId): Observable<T>;
    post(object: Txn, showMsg?: boolean, teamId?: TenantId): Observable<Txn>;
    post<T extends Txn>(object: T, showMsg = true, teamId = null): Observable<T> {
        return super.post(object, showMsg, teamId).pipe( concatMap( (t) => {
            if (t?.typeId === Txn.TYPE.PURCHASE_CN.id && t?.outstanding !== 0.00) {
                const acn = new AllocateCreditNoteAction() ;
                acn.setup(t);
                return acn.action(t, null).pipe ( map( x => {
                    if (x) {
                        return x as T; // If user did allocations, return freshly allocated version
                    } else {
                        return t as T; // Otherwise, return the saved version without allocations
                    }
                }));
            }
            return of(t);
        }));
    }

    /**
     * Every purchase document in a cycle, for the "Purchase Documents" download on the cycle page.
     *
     * Comes back as one row per document, plus a row with no attachmentId for any transaction that
     * has none. Decorated here rather than in the component, so the file names and folders are
     * settled before anything looks at the list.
     */
    getCycleDocuments(cycleId: uuid): Observable<PurchaseDocument[]> {
        const url = `${this.baseUrl}/cycleDocs/${cycleId}`;

        return this.http.get<PurchaseDocument[]>(url).pipe(
            map(rows => decoratePurchaseDocuments(
                rows.map(row => Object.assign(new PurchaseDocument(), row))
            )),
            catchError(this.handleError<PurchaseDocument[]>('Getting Purchase Documents', []))
        );
    }

    private doApproval(object: Txn, approve: boolean): Observable<Txn> {
        const url = `${this.baseUrl}/${(approve ? 'approve' : 'unapprove')}/${object.id}/${object.revision}`;
        return this.http.put<Txn>(url, object, this.httpOptions)
            .pipe(
                map((data: Txn) => {
                    return data;
                }),
                tap(() => {
                    if (approve) {
                        this.messageService.show($localize`Invoice has been approved for payment`);
                    } else {
                        this.messageService.show($localize`Invoice approval has been removed`);
                    }

                }),
                catchError(this.handleOneError<Txn>(approve ? 'Purchase Approval' : 'Purchase Unapproval', object)
                )
            )
    }

    public cnAllocate(o: Txn, allocations: Txn[]): Observable<Txn> {
        const data = {id: o.id, allocations};
        const url = `${this.baseUrl}/cnAllocate/${o.id}/${o.revision}`;
        return this.http.put<Txn>(url, data, this.httpOptions).pipe(
            catchError(this.handleOneError<Txn>('Allocating Credit Note', o)
        ));
    }

    public cnRemoveAllocation(o: Txn): Observable<Txn> {
        const url = `${this.baseUrl}/cnAllocate/${o.id}/${o.revision}`;
        return this.http.delete<Txn>(url, this.httpOptions).pipe(
            catchError(this.handleOneError<Txn>('Removing Credit Note Allocation', o)
        ));
    }

    public linkRequest(o: Txn, requestId: uuid, accept = false): Observable<ServiceRequest> {
        const url = `${this.baseUrl}/${o.id}/requests`;
        const msg = accept ? $localize`Work order linked to this invoice and accepted`
            : $localize`Work order linked to this invoice`;
        return this.http.post<ServiceRequest>(url, { requestId, accept }, this.httpOptions).pipe(
            tap(() => this.messageService.show(msg)),
            catchError(this.handleError<ServiceRequest>('Linking Work Order', null)
        ));
    }

    public unlinkRequest(o: Txn, requestId: uuid): Observable<AbstractObject> {
        const url = `${this.baseUrl}/${o.id}/requests/${requestId}`;
        return this.http.delete<AbstractObject>(url, this.httpOptions).pipe(
            tap(() => this.messageService.show($localize`Work order unlinked from this invoice`)),
            catchError(this.handleError<AbstractObject>('Unlinking Work Order', null)
        ));
    }

}
